import {Injectable, NgZone, inject} from '@angular/core'
import {environment} from '@cart/src/environments/environment'
import {LogType, Logger} from '../utils/logger'

const POSTHOG_CONFIG = {
  api_host: 'https://eu.i.posthog.com',
  person_profiles: 'identified_only',
  capture_pageview: false,
  capture_pageleave: true,
  autocapture: false,
}

interface PosthogClient {
  init(key: string, config: unknown): void
  capture(eventName: string, properties?: Record<string, unknown>): void
  identify(userId: string, properties?: Record<string, unknown>): void
  reset(): void
}

@Injectable({providedIn: 'root'})
export class PosthogService {
  readonly #zone = inject(NgZone)

  #client: PosthogClient | null = null

  constructor() {
    if (environment.analytics && environment.posthogKey) {
      this.#load()
    }
  }

  useClient(client: PosthogClient): void {
    this.#client = client
  }

  trackEvent(eventName: string, properties?: Record<string, unknown>): void {
    this.#run(() => this.#client?.capture(eventName, properties))
  }

  identifyUser(userId: string, properties?: Record<string, unknown>): void {
    this.#run(() => this.#client?.identify(userId, properties))
  }

  resetUser(): void {
    this.#run(() => this.#client?.reset())
  }

  #load(): void {
    this.#zone.runOutsideAngular(async () => {
      try {
        const client = (await import('posthog-js')).default as unknown as PosthogClient
        client.init(environment.posthogKey, POSTHOG_CONFIG)
        this.#client = client
      } catch {
        Logger.log(LogType.info, '[PostHog] the client could not be loaded')
      }
    })
  }

  #run(action: () => void): void {
    this.#zone.runOutsideAngular(() => {
      try {
        action()
      } catch {
        Logger.log(LogType.info, '[PostHog] the client rejected a call')
      }
    })
  }
}
